/**
 * The board, in the Slack list's own columns while the list is still in use (see
 * `lib/board-copy.ts`), and a Done-today strip. Cards explain themselves in plain words.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { BoardColumns, NeedsOwnerList } from '../components/board/board-columns';
import { BoardTabs, type BoardView } from '../components/board/board-tabs';
import { needsOwnerTickets } from '../lib/board-copy';
import { useLive } from '../lib/live';
import { useProvideAssistantContext } from '../lib/assistant-context';
import { boardQuery } from '../lib/queries';
import { describeRpcError, rpc } from '../lib/rpc';

export const Route = createFileRoute('/board')({
  component: Board,
});

function Board() {
  const q = useQuery({ ...boardQuery(), refetchInterval: 30_000 });
  const { team } = useLive();
  const qc = useQueryClient();
  const onMove = async (ticketId: string, stage: string, scheduledFor: string | null) => {
    await rpc.tickets.moveStage({ ticketId, stage, scheduledFor });
    await qc.invalidateQueries({ queryKey: boardQuery().queryKey });
  };
  const [view, setView] = useState<BoardView>('columns');
  useProvideAssistantContext(null);
  useCardKeys();

  if (q.error) return <div className="px-6 pt-6 text-body text-alert">{describeRpcError(q.error)}</div>;
  if (!q.data) return <div className="px-6 pt-6 text-body text-ink-3">Loading the board</div>;
  const { active, doneToday, totalActive, truncated, mirroredAt } = q.data;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-4 px-6 pt-4 pb-3">
        <h1 className="text-title font-medium">Board</h1>
        <span className="text-hint text-ink-3">
          {totalActive} active{truncated ? `, showing ${active.length}` : ''}
          {mirroredAt ? `, Slack list mirrored ${ago(mirroredAt)}` : ', Slack mirror not running yet'}
        </span>
        <div className="ml-auto">
          <BoardTabs view={view} onView={setView} needsOwner={team ? needsOwnerTickets(active, team).length : null} />
        </div>
      </div>

      {active.length === 0 ? (
        <div className="px-6 py-10 text-body text-ink-3">
          No active tickets. Once the Slack token is in place the list mirrors here within a minute; <kbd>C</kbd> logs a new one.
        </div>
      ) : view === 'needs_owner' && team ? (
        <NeedsOwnerList active={active} team={team} />
      ) : view === 'needs_owner' ? (
        <div className="px-6 py-10 text-body text-ink-3">Waiting for who is on duty in Dialpad.</div>
      ) : (
        <BoardColumns active={active} team={team} onMove={onMove} />
      )}

      {doneToday.length > 0 && (
        <div className="border-t border-line px-6 py-2 text-hint text-ink-3">
          Done today: {doneToday.length}. {doneToday.slice(0, 6).map((t) => t.subject).join(', ')}
          {doneToday.length > 6 ? ', and more' : ''}
        </div>
      )}
    </div>
  );
}

/** Arrow keys walk the cards: up and down within a column, left and right to the same row of the neighbouring column. Enter is the link's own. */
function useCardKeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      if (!(el instanceof HTMLElement) || !el.hasAttribute('data-card')) return;
      const column = el.closest<HTMLElement>('[data-column]');
      if (!column) return;
      const cards = Array.from(column.querySelectorAll<HTMLElement>('[data-card]'));
      const i = cards.indexOf(el);
      let next: HTMLElement | undefined;
      if (e.key === 'ArrowDown') next = cards[i + 1];
      else if (e.key === 'ArrowUp') next = cards[i - 1];
      else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const columns = Array.from(document.querySelectorAll<HTMLElement>('[data-column]'));
        const j = columns.indexOf(column) + (e.key === 'ArrowRight' ? 1 : -1);
        const sibling = columns[j]?.querySelectorAll<HTMLElement>('[data-card]');
        if (sibling && sibling.length > 0) next = sibling[Math.min(i, sibling.length - 1)];
      } else return;
      if (!next) return;
      e.preventDefault();
      next.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);
}

function ago(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}
